import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, Mail, Smartphone, KeyRound, Copy, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

// System Administrator 2FA step: authenticator (TOTP) primary, email OTP + recovery codes as backups.
export default function TwoFactorChallenge({ challengeId, enrolled, onSuccess, onCancel }) {
  const { setup2fa, sendEmailCode, resetAuthenticator, verify2fa, commitSession } = useAuth();
  const [mode, setMode] = useState(enrolled ? "totp" : "enroll"); // enroll | totp | email | recovery | reset
  const [code, setCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once after enrollment
  const [pendingUser, setPendingUser] = useState(null);

  const beginEnroll = useCallback(async () => {
    try { setSetupData(await setup2fa(challengeId)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [challengeId, setup2fa]);

  const enrollStarted = React.useRef(false);
  useEffect(() => {
    if (mode === "enroll" && !enrollStarted.current) {
      enrollStarted.current = true;
      beginEnroll();
    }
  }, [mode, beginEnroll]);

  const isRecovery = mode === "recovery";

  const verify = async (e) => {
    e.preventDefault();
    if (!isRecovery && code.trim().length !== 6) { toast.error("Enter the 6-digit code"); return; }
    if (isRecovery && code.trim().length < 6) { toast.error("Enter a backup code"); return; }
    setBusy(true);
    try {
      const method = isRecovery ? "recovery" : (mode === "email" ? "email" : "totp");
      const res = await verify2fa(challengeId, code.trim(), method, remember);
      if (res.recoveryCodes && res.recoveryCodes.length) {
        setPendingUser(res.user);
        setRecoveryCodes(res.recoveryCodes);
        return;
      }
      if (typeof res.recoveryRemaining === "number") {
        toast.message("Backup code used", { description: `${res.recoveryRemaining} backup code(s) remaining.` });
      }
      onSuccess(res.user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCode("");
    } finally { setBusy(false); }
  };

  const switchToEmail = async () => {
    setBusy(true);
    try {
      await sendEmailCode(challengeId);
      setMode("email"); setCode(""); setEmailSent(true);
      toast.success("We emailed you a 6-digit code.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (resetCode.trim().length < 6) { toast.error("Enter a backup code"); return; }
    setBusy(true);
    try {
      const data = await resetAuthenticator(challengeId, resetCode.trim());
      setSetupData({ secret: data.secret, otpauth_url: data.otpauth_url, qr_data_url: data.qr_data_url });
      enrollStarted.current = true;
      setCode(""); setResetCode(""); setMode("enroll");
      toast.success("Old authenticator removed. Scan the new QR code to finish.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Backup codes copied");
  };
  const downloadCodes = () => {
    const blob = new Blob([`RallyPointGo System Administrator backup codes\n\n${recoveryCodes.join("\n")}\n\nEach code works once. Keep them somewhere safe.`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rallypointgo-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Recovery-codes reveal screen (shown once, right after enrollment) ----
  if (recoveryCodes) {
    return (
      <div className="space-y-6" data-testid="twofa-recovery-codes">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <KeyRound className="h-6 w-6" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900 font-heading">Save your backup codes</p>
            <p className="text-sm text-slate-500">Use one if you ever lose your authenticator app and email.</p>
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          These are shown <strong>only once</strong>. Store them somewhere safe — each code works a single time.
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-4" data-testid="recovery-codes-list">
          {recoveryCodes.map((c) => (
            <span key={c} className="font-mono text-base font-semibold tracking-wider text-slate-800 text-center">{c}</span>
          ))}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={copyCodes} className="flex-1 rounded-full" data-testid="recovery-copy">
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
          <Button type="button" variant="outline" onClick={downloadCodes} className="flex-1 rounded-full" data-testid="recovery-download">
            <Download className="mr-2 h-4 w-4" /> Download
          </Button>
        </div>
        <Button type="button" onClick={() => { commitSession(pendingUser); onSuccess(pendingUser); }} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="recovery-continue">
          <CheckCircle2 className="mr-2 h-4 w-4" /> I've saved my codes — continue
        </Button>
      </div>
    );
  }

  const CodeInput = (
    <div className="space-y-2">
      <Label htmlFor="twofa-code">{isRecovery ? "Backup code" : "6-digit code"}</Label>
      <Input id="twofa-code" inputMode={isRecovery ? "text" : "numeric"} autoComplete="one-time-code"
        maxLength={isRecovery ? 12 : 6}
        value={code} onChange={(e) => setCode(isRecovery ? e.target.value.trim() : e.target.value.replace(/\D/g, ""))}
        placeholder={isRecovery ? "xxxx-xxxx" : "123456"}
        className={isRecovery ? "text-center text-lg font-mono tracking-widest" : "text-center text-2xl tracking-[0.5em] font-semibold"}
        data-testid="twofa-code-input" autoFocus />
    </div>
  );

  const RememberRow = (
    <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="twofa-remember-label">
      <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="twofa-remember-checkbox" />
      Remember this device for 30 days
    </label>
  );

  return (
    <div className="space-y-6" data-testid="twofa-challenge">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <p className="text-lg font-bold text-slate-900 font-heading">Two-factor verification</p>
          <p className="text-sm text-slate-500">Required for the System Administrator account.</p>
        </div>
      </div>

      {mode === "enroll" && (
        <div className="space-y-4" data-testid="twofa-enroll">
          <p className="text-sm text-slate-600">
            Set up an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy).
            Scan this QR code, then enter the 6-digit code it shows.
          </p>
          {setupData ? (
            <div className="flex flex-col items-center gap-3">
              <img src={setupData.qr_data_url} alt="Authenticator QR code" className="h-44 w-44 rounded-lg border border-slate-200"
                data-testid="twofa-qr" />
              <div className="w-full rounded-lg bg-slate-50 p-3 text-center">
                <p className="text-xs text-slate-400">Can't scan? Enter this key manually:</p>
                <p className="break-all font-mono text-sm font-semibold text-slate-700" data-testid="twofa-secret">{setupData.secret}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          )}
          <form onSubmit={verify} className="space-y-4">
            {CodeInput}
            {RememberRow}
            <Button type="submit" disabled={busy || !setupData} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="twofa-verify-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm & continue
            </Button>
          </form>
        </div>
      )}

      {mode === "totp" && (
        <form onSubmit={verify} className="space-y-4" data-testid="twofa-totp">
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Smartphone className="h-4 w-4 text-slate-400" /> Enter the 6-digit code from your authenticator app.
          </p>
          {CodeInput}
          {RememberRow}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="twofa-verify-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify & sign in
          </Button>
          <button type="button" onClick={switchToEmail} disabled={busy}
            className="flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-sky-600 hover:underline"
            data-testid="twofa-use-email">
            <Mail className="h-4 w-4" /> Use email instead
          </button>
          <button type="button" onClick={() => { setMode("recovery"); setCode(""); }} disabled={busy}
            className="flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-slate-500 hover:underline"
            data-testid="twofa-use-recovery">
            <KeyRound className="h-4 w-4" /> Use a backup code
          </button>
          <button type="button" onClick={() => { setMode("reset"); setResetCode(""); }} disabled={busy}
            className="flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-slate-400 hover:underline"
            data-testid="twofa-lost-authenticator">
            <Smartphone className="h-4 w-4" /> Lost your authenticator? Reset it
          </button>
        </form>
      )}

      {mode === "email" && (
        <form onSubmit={verify} className="space-y-4" data-testid="twofa-email">
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Mail className="h-4 w-4 text-slate-400" /> {emailSent ? "We emailed you a one-time code. Enter it below." : "Sending code…"}
          </p>
          {CodeInput}
          {RememberRow}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="twofa-verify-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify & sign in
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={switchToEmail} disabled={busy} className="font-semibold text-sky-600 hover:underline" data-testid="twofa-resend-email">
              Resend code
            </button>
            <button type="button" onClick={() => { setMode("totp"); setCode(""); }} disabled={busy} className="font-semibold text-slate-500 hover:underline" data-testid="twofa-use-authenticator">
              Use authenticator instead
            </button>
          </div>
        </form>
      )}

      {mode === "recovery" && (
        <form onSubmit={verify} className="space-y-4" data-testid="twofa-recovery">
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <KeyRound className="h-4 w-4 text-slate-400" /> Enter one of the backup codes you saved at setup. Each works once.
          </p>
          {CodeInput}
          {RememberRow}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="twofa-verify-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify & sign in
          </Button>
          <button type="button" onClick={() => { setMode("totp"); setCode(""); }} disabled={busy} className="w-full text-center text-sm font-semibold text-slate-500 hover:underline" data-testid="twofa-back-to-authenticator">
            Use authenticator instead
          </button>
          <button type="button" onClick={() => { setMode("reset"); setResetCode(""); }} disabled={busy} className="w-full text-center text-sm font-semibold text-slate-400 hover:underline" data-testid="twofa-lost-authenticator-recovery">
            Lost your authenticator? Reset it with a backup code
          </button>
        </form>
      )}

      {mode === "reset" && (
        <form onSubmit={doReset} className="space-y-4" data-testid="twofa-reset">
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Smartphone className="h-4 w-4 text-slate-400" /> Lost access to your authenticator app? Enter one of your saved backup codes to remove it and set up a new one right now.
          </p>
          <div className="space-y-2">
            <Label htmlFor="reset-code">Backup code</Label>
            <Input id="reset-code" inputMode="text" maxLength={12} value={resetCode}
              onChange={(e) => setResetCode(e.target.value.trim())} placeholder="xxxx-xxxx"
              className="text-center text-lg font-mono tracking-widest" data-testid="twofa-reset-input" autoFocus />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="twofa-reset-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset authenticator
          </Button>
          <button type="button" onClick={() => { setMode(enrolled ? "totp" : "enroll"); setResetCode(""); }} disabled={busy} className="w-full text-center text-sm font-semibold text-slate-500 hover:underline" data-testid="twofa-reset-cancel">
            Back
          </button>
        </form>
      )}

      {onCancel && (
        <button type="button" onClick={onCancel} className="w-full text-center text-sm text-slate-400 hover:underline" data-testid="twofa-cancel">
          Back to sign in
        </button>
      )}
    </div>
  );
}
